/// <reference path="../../typings/browser.d.ts" />

namespace Application.Controllers {
    'use strict';

    /**
     * Custom route params interface
     */
    export interface IRouteProbeJobsParams extends ng.route.IRouteParamsService {
        probeId: number;
    }

    /**
     * ProbeJobsController class
     */
    export class ProbeJobsController {
        public static $name: string = "probeJobsController";
        public static $inject: string[] = ['$scope',
                                           'networkNodeService',
                                           'networkNodeTypeService',
                                           'probeService',
                                           'portScannerJobService',
                                           'mvpAppScannerJobService',
                                           '$routeParams',
                                           '$location',
                                           'ModalService',
                                           '$interval',
                                           '$timeout',
                                           'messageTranslationsService'];

        private scope: ng.IScope;
        private networkNodeService: Application.Services.NetworkNodeService;
        private networkNodeTypeService: Application.Services.NetworkNodeTypeService;
        private probeService: Application.Services.ProbeService;
        private portScannerJobService: Application.Services.PortScannerJobService;
        private mvpAppScannerJobService: Application.Services.MVPAppScannerJobService;
        private routeParams: IRouteProbeJobsParams;
        private location: ng.ILocationService;
        private modalService: ngModal.IModalService;
        private interval: angular.IIntervalService;
        private intervalPromise: ng.IPromise<Object>;
        private messageTranslationsService: Application.Services.MessageTranslationsService;

        private networkNodes: Models.INetworkNode[];
        private networkNodeTypes: Models.INetworkNodeType[];
        private probe: Models.IProbe;
        private jobs: Models.IJob[];
        private jobsToMonitorMap: { [id: string]: Models.IJob };

        /**
         * Constructor of the class.
         */
        constructor(scope: ng.IScope,
                    networkNodeService: Application.Services.NetworkNodeService,
                    networkNodeTypeService: Application.Services.NetworkNodeTypeService,
                    probeService: Application.Services.ProbeService,
                    portScannerJobService: Application.Services.PortScannerJobService,
                    mvpAppScannerJobService: Application.Services.MVPAppScannerJobService,
                    routeParams: IRouteProbeJobsParams,
                    location: ng.ILocationService,
                    modalService: ngModal.IModalService,
                    interval: angular.IIntervalService,
                    $timeout: ng.ITimeoutService,
                    messageTranslationsService: Services.MessageTranslationsService ) {
            $timeout(() => {
                window.altair_admin_common_init();
            }, 500);
            this.scope = scope;
            this.networkNodeService = networkNodeService;
            this.networkNodeTypeService = networkNodeTypeService;
            this.probeService = probeService;
            this.portScannerJobService = portScannerJobService;
            this.mvpAppScannerJobService = mvpAppScannerJobService;
            this.routeParams = routeParams;
            this.location = location;
            this.modalService = modalService;
            this.interval = interval;
            this.messageTranslationsService = messageTranslationsService;
            this.initData();
        }

        /**
         * Get style bg class from the job status
         */
        public getBgClassFromJobStatus(job: Models.IJob): string {
            if (job.jobStatus === Models.JobStatus.CREATED)
                return "md-bg-light-green-100";
            if (job.jobStatus === Models.JobStatus.RUNNING)
                return "md-bg-yellow-100";
            if (job.jobStatus === Models.JobStatus.FINISHED)
                return "md-bg-light-blue-100";
            if (job.jobStatus === Models.JobStatus.ERROR)
                return "md-bg-red-100";
        }

        /**
         * Get style bg class from the job status
         */
        public getBadgeClassFromJobStatus(job: Models.IJob): string {
            if (job.jobStatus === Models.JobStatus.CREATED)
                return "uk-badge-success";
            if (job.jobStatus === Models.JobStatus.RUNNING)
                return "uk-badge-warning";
            if (job.jobStatus === Models.JobStatus.FINISHED)
                return "uk-badge-primary";
            if (job.jobStatus === Models.JobStatus.ERROR)
                return "uk-badge-danger";
        }

        /**
         * getBadgeClassFromPortStatus
         */
        public getBadgeClassFromPortStatus(status: string): string {
            if (status === "open")
                return "uk-badge-success";
            if (status === "open|filtered")
                return "uk-badge-warning";
            if (status === "filtered")
                return "uk-badge-warning";
            if (status === "closed")
                return "uk-badge-danger";

            return "uk-badge-danger";
        }

        /**
         * Get style bg class from result
         */
        public getBadgeClassFromResult(result: Models.JobItemResult): string {
            if (result === Models.JobItemResult.OK)
                return "uk-badge-success";
            if (result === Models.JobItemResult.FAIL)
                return "uk-badge-danger";
        }

        public isJobOnCreatedStatus(job: Models.IJob): boolean {
            return job.jobStatus === Models.JobStatus.CREATED;
        }

        public isJobOnRunningStatus(job: Models.IJob): boolean {
            return job.jobStatus === Models.JobStatus.RUNNING;
        }

        public isJobOnFinishedStatus(job: Models.IJob): boolean {
            return job.jobStatus === Models.JobStatus.FINISHED;
        }

        public isJobOnErrorStatus(job: Models.IJob): boolean {
            return job.jobStatus === Models.JobStatus.ERROR;
        }

        public createNewPortScannerJob(): void {
            if (this.probe && this.probe.isConnectedToNetwork()) {
                // probe is connected
                this.modalService.showModal({
                    templateUrl: Application.Controllers.PortScannerJobModalController.templateURL,
                    controller: Application.Controllers.PortScannerJobModalController.$name,
                    inputs: {
                        networkNodes: this.networkNodes,
                        nodeTypes: this.networkNodeTypes,
                    },
                }).then((modalJob: ngModal.IModalObject) => {
                    let modalElem: UIkit.ModalElement = UIkit.modal(Application.Controllers.PortScannerJobModalController.ID,
                                                        {bgclose: false, modal: false, keyboard: false});
                    modalElem.show();
                    modalJob.close.then((jobInfo: Models.IJobInfo) => {
                        modalElem.hide();
                        if (jobInfo) {
                            // calls to create the job
                            let jobRequest: Models.IJobRequest = new Models.PortScannerJobRequest();
                            jobRequest.probeId = Common.Utils.idFromHref(this.probe.href);
                            jobRequest.jobInfo = jobInfo;

                            this.portScannerJobService.createPortScannerJob(jobRequest)
                            .then((job: Models.IJob) => {
                                this.jobs.unshift(job);
                                this.jobsToMonitorMap[job.jobId] = job;
                            }).catch(() => {
                                this.messageTranslationsService.alertOnError("Error creating new job", "top-right");
                            });

                            Common.Utils.alertOnInfo("Creating job ...", "top-right");
                        }
                    });
                });
            } else {
                // probe is not connected!
                this.showProbeNotConnectedModal();
            }
        }

        public createNewMVPAppScannerJob(): void {
            if (this.probe && this.probe.isConnectedToNetwork()) {
                // probe is connected
                this.modalService.showModal({
                    templateUrl: Application.Controllers.SearchNodeModalController.templateURL,
                    controller: Application.Controllers.SearchNodeModalController.$name,
                    inputs: {
                        networkNodes: this.networkNodes,
                    },
                }).then((modal: ngModal.IModalObject) => {
                    let modalElem: UIkit.ModalElement = UIkit.modal(Application.Controllers.SearchNodeModalController.ID,
                                                        {bgclose: false, modal: false, keyboard: false});
                    modalElem.show();
                    modal.close.then((node: Models.INetworkNode) => {
                        modalElem.hide();
                        if (node) {
                            // calls to create the job
                            let jobRequest: Models.IJobRequest = new Models.MVPAppScannerJobRequest();
                            jobRequest.probeId = Common.Utils.idFromHref(this.probe.href);
                            jobRequest.jobInfo.networkElementHref = Common.Utils.idFromHref(node.href);
                            jobRequest.jobInfo.networkNode = node;
                            jobRequest.jobInfo.networkNodeType = node.type;

                            this.mvpAppScannerJobService.createMVPAppScannerJob(jobRequest)
                            .then((job: Models.IJob) => {
                                this.jobs.unshift(job);
                                this.jobsToMonitorMap[job.jobId] = job;
                            }).catch(() => {
                                this.messageTranslationsService.alertOnError("Error creating new job", "top-right");
                            });

                            Common.Utils.alertOnInfo("Creating job ...", "top-right");
                        }
                    });
                });
            } else {
                // probe is not connected!
                this.showProbeNotConnectedModal();
            }
        }

        public removeJob(job: Models.IJob): void {
            this.modalService
            .showModal({
                templateUrl: Application.Controllers.ConfirmModalController.templateURL,
                controller: Application.Controllers.ConfirmModalController.$name,
                inputs: {
                    title: 'DELETE Port scanner job',
                    description: "This action cannot be undone.",
                    name: "",
                    type: 'information',
                },
            })
            .then((modal: ngModal.IModalObject) => {
                let modalElem: UIkit.ModalElement =
                    UIkit.modal(Application.Controllers.ConfirmModalController.ID,
                        { bgclose: false, modal: false, keyboard: false });
                modalElem.show();
                modal.close.then((result: Object) => {
                    modalElem.hide();
                    if (result) {
                        let foundId: number;
                        for (let i: number = 0; i < this.jobs.length; i++) {
                            if (this.jobs[i].jobId === job.jobId) {
                                foundId = i;
                                break;
                            }
                        }

                        this.jobs.splice(foundId, 1);
                        delete this.jobsToMonitorMap[job.jobId];

                        if (this.isPortScannerJob(job)) {
                            this.portScannerJobService.deleteJobById(job.jobId).success((resultDelete: boolean) => {
                                // ok
                            }).error((resultDelete: boolean) => {
                                this.messageTranslationsService.alertOnError("Error removing job");
                            });
                        } else if (this.isMVPAppScannerJob(job)) {
                            this.mvpAppScannerJobService.deleteJobById(job.jobId).success((resultDelete: boolean) => {
                                // ok
                            }).error((resultDelete: boolean) => {
                                this.messageTranslationsService.alertOnError("Error removing job");
                            });
                        }
                    }
                });
            });
        }

        public isPortScannerJob(job: Models.IJob): boolean  {
            return job.jobType === Models.JobType.PORT_SCANNER;
        }

        public isMVPAppScannerJob(job: Models.IJob): boolean  {
            return job.jobType === Models.JobType.MVP_APP_SCANNER;
        }

        private showProbeNotConnectedModal(): void {
            this.modalService.showModal({
                templateUrl: Application.Controllers.InfoModalController.templateURL,
                controller: Application.Controllers.InfoModalController.$name,
                inputs: {
                    title: 'Probe not connected',
                    description: "The current probe is not connected to a network. Please, connect it before creating a job.",
                },
            }).then((modal: ngModal.IModalObject) => {
                let modalElem: UIkit.ModalElement = UIkit.modal(Application.Controllers.InfoModalController.ID,
                                                    {bgclose: false, modal: false, keyboard: false});
                modalElem.show();
                modal.close.then((result: Object) => {
                    modalElem.hide();
                });
            });
        }

        private loadProbe(): void {
            this.probeService.getProbeById(this.routeParams.probeId, (probe: Models.IProbe) => {
                this.probe = probe;
            });
        }

        private loadNetworkNodeTypes(): void {
            this.networkNodeTypeService
                .getAllNetworkNodeTypes()
                .then((networkNodeTypes: Application.Models.INetworkNodeType[]) => {
                    this.networkNodeTypes = networkNodeTypes;
                });
        }

        private loadNetworkNodes(): void {
            this.networkNodeService.getAllNetworkNodes((nodes: Models.NetworkNode[]) => {
                this.networkNodes = nodes;
            });
        }

        private loadJobs(): void {
            // tODO: loading jobs from two sources...
            // unify on a single backend call? or at least on a single frontend service...
            this.portScannerJobService.getJobsByProbeId(this.routeParams.probeId)
            .then((jobs1: Models.IJob[]) => {
                this.jobs = jobs1;

                for (let job of jobs1) {
                    if (job.jobStatus === Models.JobStatus.CREATED ||
                        job.jobStatus === Models.JobStatus.RUNNING) {
                        this.jobsToMonitorMap[job.jobId] = job;
                    }
                }

                this.mvpAppScannerJobService.getJobsByProbeId(this.routeParams.probeId)
                .then((jobs2: Models.IJob[]) => {
                    this.jobs = this.jobs.concat(jobs2);

                    for (let job of jobs2) {
                        if (job.jobStatus === Models.JobStatus.CREATED ||
                            job.jobStatus === Models.JobStatus.RUNNING) {
                            this.jobsToMonitorMap[job.jobId] = job;
                        }
                    }
                }).catch(() => {
                    this.messageTranslationsService.alertOnError("Error loading avaialable app scanner jobs");
                });


            }).catch(() => {
                this.messageTranslationsService.alertOnError("Error loading avaialable port scanner jobs");
            });
        }

        /**
         * Initiliaze data
         */
        private initData(): void {
            this.jobs = [];
            this.jobsToMonitorMap = {};
            this.loadProbe();
            this.loadNetworkNodeTypes();
            this.loadNetworkNodes();
            this.loadJobs();

            this.intervalPromise = this.interval((): void => {
                // checks for created/running jobs
                if (Common.Utils.sizeOf(this.jobsToMonitorMap) > 0) {
                    for (let key in this.jobsToMonitorMap) {
                        if (this.jobsToMonitorMap.hasOwnProperty(key)) {
                            if (this.isPortScannerJob(this.jobsToMonitorMap[key])) {
                                this.portScannerJobService.getJobById(this.jobsToMonitorMap[key].jobId)
                                .then((job: Models.IJob) => {
                                    this.stopMonitoringJob(job);
                                });
                            } else if (this.isMVPAppScannerJob(this.jobsToMonitorMap[key])) {
                                this.mvpAppScannerJobService.getJobById(this.jobsToMonitorMap[key].jobId)
                                .then((job: Models.IJob) => {
                                    this.stopMonitoringJob(job);
                                });
                            }
                        }
                    }
                }
            }, 2000);

            this.scope.$on('$destroy', () => {
                if (this.intervalPromise)
                    this.interval.cancel(this.intervalPromise);
            });
        }

        private stopMonitoringJob(job: Models.IJob): void {
            for (let i: number = 0; i < this.jobs.length; i++) {
                if (this.jobs[i].jobId === job.jobId &&
                    this.jobs[i].jobStatus !== job.jobStatus) {
                    this.jobs[i] = job;
                    break;
                }
            }

            if (job.jobStatus === Models.JobStatus.ERROR ||
                job.jobStatus === Models.JobStatus.FINISHED)
                delete this.jobsToMonitorMap[job.jobId];
        }
    }
}